import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ExternalLink, AlertCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/brand";

const ABLEFY_DASHBOARD_URL = "https://myablefy.com/users/sign_in";

function KuendigungDialog({ trigger }) {
  return (
    <Dialog>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className="bg-zinc-950 border-zinc-800 text-white max-w-md"
        data-testid="kuendigung-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/15 border border-cyan-500/40 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-cyan-400" />
            </div>
            <DialogTitle
              className="text-xl font-bold tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Abo kündigen
            </DialogTitle>
          </div>
          <DialogDescription className="text-sm text-zinc-400 leading-relaxed pt-2">
            Die Kündigung erfolgt über <strong className="text-white">Ablefy</strong> — unseren
            Zahlungsabwickler. Du hast zwei einfache Wege:
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <div className="p-4 rounded-xl bg-zinc-900/60 border border-zinc-800" data-testid="kuendigung-option-1">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 text-xs font-bold flex items-center justify-center mt-0.5">1</div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-white mb-1">Über dein Ablefy-Konto</p>
                <p className="text-xs text-zinc-400 leading-relaxed mb-3">
                  Logge dich in dein Ablefy-Konto ein und verwalte deine Bestellung dort.
                </p>
                <a
                  href={ABLEFY_DASHBOARD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-cyan-300 hover:text-cyan-200"
                  data-testid="kuendigung-ablefy-link"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Zu Ablefy
                </a>
              </div>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-zinc-900/60 border border-zinc-800" data-testid="kuendigung-option-2">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 text-xs font-bold flex items-center justify-center mt-0.5">2</div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-white mb-1">Über deine Bestätigungs-E-Mail</p>
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Öffne die E-Mail, die du nach deinem Kauf von Ablefy bekommen hast — dort findest du
                  einen Link zur Bestellverwaltung. Über diesen Link kannst du deine Kündigung direkt auslösen.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs text-zinc-500 pt-2">
            <Mail className="w-3.5 h-3.5" />
            <span>Bei Fragen: <a href="mailto:info@viral-cut-ai.de" className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2">info@viral-cut-ai.de</a></span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Footer({ variant = "public" }) {
  // variant "public" = used on /, /auth (subtle bg)
  // variant "app"    = used inside the dashboard (slightly darker)
  return (
    <footer
      className={`relative z-10 border-t border-zinc-800/60 mt-16 sm:mt-24 ${variant === "app" ? "bg-black/30" : "bg-transparent"}`}
      data-testid="site-footer"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 flex flex-col items-center gap-6">
        {/* InnovAItion Logo + tagline */}
        <a href="#" onClick={(e) => e.preventDefault()} className="flex flex-col items-center gap-1.5 group" data-testid="footer-brand">
          <img
            src={BRAND.logo}
            alt="InnovAItion"
            className="h-12 sm:h-14 w-auto opacity-90 group-hover:opacity-100 transition-opacity"
          />
          <p className="text-[10px] tracking-[0.25em] uppercase font-semibold" style={{ color: BRAND.colors.cyan }}>
            powered by InnovAItion
          </p>
        </a>

        <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-zinc-800/60">
          <p className="text-xs text-zinc-500">
            © {new Date().getFullYear()} {BRAND.name} · Erfolg beginnt in dir
          </p>

          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs">
            <Link to="/impressum" className="text-zinc-400 hover:text-white transition-colors" data-testid="footer-link-impressum">
              Impressum
            </Link>
            <Link to="/agb" className="text-zinc-400 hover:text-white transition-colors" data-testid="footer-link-agb">
              AGB
            </Link>
            <Link to="/datenschutz" className="text-zinc-400 hover:text-white transition-colors" data-testid="footer-link-datenschutz">
              Datenschutz
            </Link>
            <KuendigungDialog
              trigger={
                <button
                  className="text-zinc-400 hover:text-cyan-300 transition-colors"
                  data-testid="footer-link-kuendigung"
                >
                  Kündigung
                </button>
              }
            />
          </nav>
        </div>
      </div>
    </footer>
  );
}

export { KuendigungDialog };
